import { createTheme, type Theme, type ThemeOptions } from '@mui/material';

// ---------------------------------------------------------------------------
// Theme Preferences
// ---------------------------------------------------------------------------

export interface ThemePreferences {
  mode: 'dark' | 'light' | 'system';
  highContrast: boolean;
  fontFamily: 'default' | 'dyslexic';
  fontSize: 'small' | 'medium' | 'large' | 'xl';
  reducedMotion: boolean;
}

export const defaultPreferences: ThemePreferences = {
  mode: 'dark',
  highContrast: false,
  fontFamily: 'default',
  fontSize: 'medium',
  reducedMotion: false,
};

// ---------------------------------------------------------------------------
// Palette definitions
// ---------------------------------------------------------------------------

const darkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  primary: {
    main: '#6C9BFF',
    light: '#9DBFFF',
    dark: '#3D6FCC',
  },
  secondary: {
    main: '#FF8A65',
    light: '#FFBB93',
    dark: '#C75B39',
  },
  background: {
    default: '#0A0E17',
    paper: '#121829',
  },
  error: { main: '#FF5252' },
  warning: { main: '#FFB74D' },
  success: { main: '#69F0AE' },
  text: {
    primary: '#E8ECF4',
    secondary: '#8892A8',
  },
  divider: 'rgba(255,255,255,0.08)',
};

const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: '#3D6FCC',
    light: '#6C9BFF',
    dark: '#2A4E8F',
  },
  secondary: {
    main: '#C75B39',
    light: '#FF8A65',
    dark: '#9A3F24',
  },
  background: {
    default: '#FAFBFD',
    paper: '#FFFFFF',
  },
  error: { main: '#D32F2F' },
  warning: { main: '#ED6C02' },
  success: { main: '#2E7D32' },
  text: {
    primary: '#1A1F2E',
    secondary: '#5A6478',
  },
  divider: 'rgba(0,0,0,0.08)',
};

const highContrastDarkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  primary: {
    main: '#80B0FF',
    light: '#B3D1FF',
    dark: '#4D7FE6',
  },
  secondary: {
    main: '#FFA080',
    light: '#FFC4B0',
    dark: '#E06040',
  },
  background: {
    default: '#000000',
    paper: '#0D0D0D',
  },
  error: { main: '#FF6B6B' },
  warning: { main: '#FFC857' },
  success: { main: '#7DFFBE' },
  text: {
    primary: '#FFFFFF',
    secondary: '#B0B8CC',
  },
  divider: 'rgba(255,255,255,0.2)',
};

const highContrastLightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: '#1A3D7A',
    light: '#3D6FCC',
    dark: '#0D2650',
  },
  secondary: {
    main: '#8B3A1F',
    light: '#C75B39',
    dark: '#5C2614',
  },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
  },
  error: { main: '#B71C1C' },
  warning: { main: '#BF360C' },
  success: { main: '#1B5E20' },
  text: {
    primary: '#000000',
    secondary: '#1A1A1A',
  },
  divider: 'rgba(0,0,0,0.2)',
};

// ---------------------------------------------------------------------------
// Font size map (base htmlFontSize)
// ---------------------------------------------------------------------------

const fontSizeMap: Record<ThemePreferences['fontSize'], number> = {
  small: 12,
  medium: 14,
  large: 16,
  xl: 18,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPalette(
  resolvedMode: 'dark' | 'light',
  highContrast: boolean,
): ThemeOptions['palette'] {
  if (highContrast) {
    return resolvedMode === 'dark' ? highContrastDarkPalette : highContrastLightPalette;
  }
  return resolvedMode === 'dark' ? darkPalette : lightPalette;
}

function getFontFamily(font: ThemePreferences['fontFamily']): string {
  if (font === 'dyslexic') {
    return '"OpenDyslexic", "Inter", sans-serif';
  }
  return '"Inter", "SF Pro Display", -apple-system, sans-serif';
}

function getCardBorder(resolvedMode: 'dark' | 'light', highContrast: boolean): string {
  if (highContrast) {
    return resolvedMode === 'dark'
      ? '1px solid rgba(255,255,255,0.2)'
      : '1px solid rgba(0,0,0,0.2)';
  }
  return resolvedMode === 'dark'
    ? '1px solid rgba(255,255,255,0.06)'
    : '1px solid rgba(0,0,0,0.06)';
}

function getSubtitleColor(resolvedMode: 'dark' | 'light', highContrast: boolean): string {
  if (highContrast) {
    return resolvedMode === 'dark' ? '#B0B8CC' : '#1A1A1A';
  }
  return resolvedMode === 'dark' ? '#8892A8' : '#5A6478';
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createCadenceTheme(prefs: Partial<ThemePreferences> = {}): Theme {
  const merged: ThemePreferences = { ...defaultPreferences, ...prefs };

  // Resolve 'system' to 'dark' when called directly (ThemeContext handles the
  // actual system preference detection). Default to dark for backward compat.
  const resolvedMode: 'dark' | 'light' =
    merged.mode === 'system' ? 'dark' : merged.mode;

  const palette = getPalette(resolvedMode, merged.highContrast);
  const fontFamily = getFontFamily(merged.fontFamily);
  const baseFontSize = fontSizeMap[merged.fontSize];
  const subtitleColor = getSubtitleColor(resolvedMode, merged.highContrast);
  const cardBorder = getCardBorder(resolvedMode, merged.highContrast);

  // Focus indicator styles shared across interactive components
  const focusVisibleStyle = {
    '&.Mui-focusVisible': {
      outline: '2px solid',
      outlineColor: 'primary.main',
      outlineOffset: 2,
    },
  };

  // Build components map — only include MuiCssBaseline when reducedMotion is on
  const components: ThemeOptions['components'] = {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: cardBorder,
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 600,
          borderRadius: 8,
          ...focusVisibleStyle,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          ...focusVisibleStyle,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  };

  if (merged.reducedMotion) {
    components.MuiCssBaseline = {
      styleOverrides: {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          transitionDuration: '0.01ms !important',
        },
      },
    };
  }

  const themeOptions: ThemeOptions = {
    palette,
    typography: {
      fontFamily,
      fontSize: baseFontSize,
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 600,
      },
      subtitle1: {
        fontWeight: 500,
        color: subtitleColor,
      },
      body2: {
        color: subtitleColor,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components,
  };

  return createTheme(themeOptions);
}

// ---------------------------------------------------------------------------
// Default export — backward-compatible dark theme
// ---------------------------------------------------------------------------

const theme = createCadenceTheme(defaultPreferences);
export default theme;
